import { useOverlayTrigger } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import PropTypes from 'prop-types';
import React__default, { useRef, useEffect, useMemo } from 'react';
import { SwanPopoverContextProvider } from './popover.context.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useDarkModeExtractor } from '../../hooks/use-dark-mode-extractor.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

const propTypes = {
    /**
   * @deprecated control the open state with a ref on the trigger element instead
   *
   * Should it be expanded by default or not?
   */ expanded: deprecatedProp(PropTypes.bool, 'Control the open state with a ref on the trigger element instead'),
    /**
   * Position in which should the popover be shown
   */ position: PropTypes.oneOf([
        'top',
        'right',
        'bottom',
        'left'
    ])
};
const propKeysToRemove = Object.keys(propTypes);
const Popover = renderWithRef('Popover', propTypes, (props, ref)=>{
    useComponentStylesLoaded('Popover', SWAN_STYLE_KEY_MAP.popover);
    const { position, expanded, children } = props;
    const popoverRef = useRef(null);
    const combinedRef = assignRefs(ref, popoverRef);
    const triggerRef = useRef(null);
    const overlayState = useOverlayTriggerState({});
    const hasDarkMode = useDarkModeExtractor(popoverRef, overlayState.isOpen);
    const { overlayProps, triggerProps } = useOverlayTrigger({
        type: 'dialog'
    }, overlayState, triggerRef);
    useEffect(()=>{
        if (expanded) {
            overlayState.open();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        expanded
    ]);
    const contextValue = useMemo(()=>({
            hasDarkMode,
            popoverRef,
            triggerRef,
            overlayState,
            overlayProps: overlayProps,
            triggerProps: triggerProps,
            popoverPlacement: position !== null && position !== void 0 ? position : 'top'
        }), [
        triggerRef,
        popoverRef,
        overlayState,
        overlayProps,
        triggerProps,
        position,
        hasDarkMode
    ]);
    const classNames = [
        'swan-popover-container'
    ];
    return /*#__PURE__*/ React__default.createElement(SwanPopoverContextProvider, {
        value: contextValue
    }, /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "div",
        forwardedRef: combinedRef,
        classNames: classNames,
        props: props,
        propKeysToRemove: propKeysToRemove
    }, children));
});

export { Popover };
